import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useInterfaceStore } from './interfaceStore'
import { useFilterStore } from 'Store/filterStore'
import { useKeyboardStore } from 'Store/keyboardStore'
//Util
import { v4 as uuid } from 'uuid'
import { cloneDeep } from 'lodash' // Changed from clone to cloneDeep for safety
//Types
import type { Ref } from 'vue'
import {
  AnnotationTypes,
  AnnotationsStatuses,
  MarkerTypes,
  MeasurementStatus,
} from 'Modules/types/pages/imageReview/enums'
import {
  AnnotationUpdateWithAtLeastOneKey,
  ImageAnnotation,
  ImageAnnotationType,
} from 'Modules/types/pages/imageReview/imageAnnotationType'
import { useMarkerStore } from './markerStore'
import { DebounceManager } from '@/modules/pages/imageReview/DebounceManager'
import { useServerStore } from './serverStore'
import {
  getPointsVertices,
  getRectangleVertices,
  getGroupedAnnotationsMap,
  getGroupedAnnotationsArray,
} from '@/modules/pages/imageReview/helperFunctions'
import { useMeasurementStore } from './measurementsStore'
import { ImageFilters } from '@/modules/types/filterTypes'

type GroupedAnnotationMap = {
  [key: number]: ImageAnnotationType[]
  ungrouped: ImageAnnotationType[]
}

function annotationSort(a, b) {
  const typeSort = a.annotationType.localeCompare(b.annotationType)
  if (typeSort !== 0) {
    return typeSort
  }
  const statusSort = (b.annotationStatus || '').localeCompare(a.annotationStatus || '')
  if (statusSort !== 0) {
    return statusSort
  }
  return b.confidence - a.confidence
}

export const useAnnotationsStore = defineStore('ImageReview_Annotations', () => {
  //State
  const measurementStore = useMeasurementStore()
  const interfaceStore = useInterfaceStore()
  const serverStore = useServerStore()
  const markerStore = useMarkerStore()
  const keyboardStore = useKeyboardStore()
  const filterStore = useFilterStore()

  const annotationsData = ref<Array<ImageAnnotation>>([])
  const groupAnnotations = ref<boolean>(false)

  // TODO Remove currentAnnotations entirely
  // Detailed in ticket: https://gitlab.com/near-earth/inspection/aci-portfolio/jeteye-review/-/issues/250

  // TODO Skim down staged annotations as they no longer need to contain
  // all of the image annotation information.
  // Detailed in ticket: https://gitlab.com/near-earth/inspection/aci-portfolio/jeteye-review/-/issues/251

  const stagedAnnotations: Ref<Array<ImageAnnotation>> = ref([])
  const currentAnnotation = computed(() => stagedAnnotations.value[0])
  const isSuggestionsHidden = computed<boolean>(() => filterStore.filterState.image.includes(ImageFilters.Suggestions))

  const debounceManager = new DebounceManager(serverStore.handleAnnotationBoundsUpdate)

  //Getters
  const annotations = computed(() => {
    if (isSuggestionsHidden.value === true) {
      return annotationsData.value
        .filter((annotation: ImageAnnotation) => annotation.isAnnotation === true)
        .sort(annotationSort)
    }
    return annotationsData.value
      .filter(
        (annotation: ImageAnnotation) =>
          annotation.annotationStatus !== AnnotationsStatuses.Rejected && annotation.pendingDeletion !== true
      )
      .sort(annotationSort)
  })

  const confirmedAnnotations = computed<Array<ImageAnnotationType>>(() => {
    return annotations.value?.filter((annotation: ImageAnnotationType) => {
      return annotation.isAnnotation && !annotation.isTemp
    })
  })

  const suggestionsAnnotations = computed<Array<ImageAnnotationType>>(() => {
    return annotations.value?.filter((annotation: ImageAnnotationType) => {
      return !annotation.isAnnotation
    })
  })

  const groupedAnnotationsMap = computed<GroupedAnnotationMap>(() => {
    return getGroupedAnnotationsMap(confirmedAnnotations.value)
  })

  const groupedAnnotationsArray = computed<Array<Array<ImageAnnotationType> | ImageAnnotationType>>(() => {
    return getGroupedAnnotationsArray(groupedAnnotationsMap.value)
  })

  const groupedSuggestionsMap = computed<GroupedAnnotationMap>(() => {
    return getGroupedAnnotationsMap(suggestionsAnnotations.value)
  })

  const groupedSuggestionsArray = computed<Array<Array<ImageAnnotationType> | ImageAnnotationType>>(() => {
    return getGroupedAnnotationsArray(groupedSuggestionsMap.value)
  })

  const isAllStagedAnnotationInSameGroup = computed<boolean | undefined>(() => {
    if (stagedAnnotations.value.length === 0) return
    if (stagedAnnotations.value.length === 1 && stagedAnnotations.value[0]?.annotationGroup) return true

    const firstGroupId = stagedAnnotations.value[0]?.annotationGroup

    if (!firstGroupId) return false
    return stagedAnnotations.value.every((annotation) => annotation.annotationGroup === firstGroupId)
  })

  const isStagedAnnotationsInDifferentGroups = computed<boolean>(() => {
    const uniqueGroupIds = new Set()
    stagedAnnotations.value.forEach((annotation) => {
      const groupId = annotation.annotationGroup
      if (groupId !== undefined) {
        uniqueGroupIds.add(groupId)
      }
    })
    return uniqueGroupIds.size > 1
  })

  const isStagedAnnotationsConsistent = computed<boolean>(() => {
    if (stagedAnnotations.value.length === 0) return true
    const {
      defectType: firstDefectType,
      defectStatus: firstDefectStatus,
      defectComment: firstDefectComment,
    } = stagedAnnotations.value[0]

    // Check if all annotations have the same defectType, defectStatus, and defectComment
    return stagedAnnotations.value.every((annotation) => {
      return (
        annotation.defectType === firstDefectType &&
        annotation.defectStatus === firstDefectStatus &&
        annotation.defectComment === firstDefectComment
      )
    })
  })

  //Actions
  function createTempAnnotation(defectBounds: number[][]): string {
    const id = uuid()
    const annotation = new ImageAnnotation(
      null,
      id,
      AnnotationTypes.Annotation,
      defectBounds,
      null,
      null,
      null,
      true,
      true,
      null
    )
    stagedAnnotations.value = [annotation]
    return id
  }

  function setAnnotations(newAnnotations: Array<ImageAnnotationType>): void {
    annotationsData.value = newAnnotations.filter(
      (annotation: ImageAnnotationType) => annotation.annotationStatus !== AnnotationsStatuses.Rejected
    )
  }

  function removeAnnotations(): void {
    annotationsData.value = []
    debounceManager.clear()
  }

  function removeAnnotationByUuid(uuid: string): void {
    annotationsData.value = annotationsData.value.filter(
      (annotation: ImageAnnotationType) => annotation.annotationUuid !== uuid
    )
  }

  function removeTempAnnotation(): void {
    annotationsData.value = annotationsData.value.filter((annotation: ImageAnnotationType) => {
      const isTempAnnotation = annotation.isTemp === true
      return !isTempAnnotation
    })
  }

  function getAnnotationByUuid(uuid: string) {
    return annotationsData.value.find((annotation) => annotation.annotationUuid === uuid)
  }

  function getStagedAnnotationByUuid(uuid: string) {
    return stagedAnnotations.value.find((annotation) => annotation.annotationUuid === uuid)
  }

  function clearHoveredAnnotations() {
    annotationsData.value.forEach((annotation) => {
      if (annotation.isHovered) {
        annotation.isHovered = false
      }
    })
  }

  function clearStagedAnnotations() {
    stagedAnnotations.value = []
  }

  function unstageAnnotationByUuid(uuid: string) {
    try {
      if (!uuid) {
        throw `annotationsStore:unstageAnnotationByUuid: No uuid was provided.`
      }
      stagedAnnotations.value = stagedAnnotations.value.filter((annotation) => annotation.annotationUuid !== uuid)
    } catch (error) {
      console.error(`Error filtering out staged annotations for uuid ${uuid}`, error)
    }
  }

  function stageAnnotationByUuid(uuid: string) {
    const isStaged = stagedAnnotations.value.find((annotation) => annotation.annotationUuid === uuid) !== undefined
    if (!isStaged) {
      const annotation = getAnnotationByUuid(uuid)
      stagedAnnotations.value = [...stagedAnnotations.value, annotation]
    }
  }

  function hoverAnnotationByUuid(uuid: string) {
    const annotation = annotationsData.value.find((a) => a.annotationUuid === uuid)
    if (annotation && !annotation.isHovered) {
      annotation.isHovered = true
    }
  }

  function unhoverAnnotationByUuid(uuid: string) {
    const annotation = annotationsData.value.find((a) => a.annotationUuid === uuid)
    if (annotation && annotation.isHovered) {
      annotation.isHovered = false
    }
  }

  function updateStagedAnnotationValues(newVal: AnnotationUpdateWithAtLeastOneKey) {
    stagedAnnotations.value.forEach((annotation) => {
      annotation.defectType = newVal.defectType ?? annotation.defectType
      annotation.defectStatus = newVal.defectStatus ?? annotation.defectStatus
      annotation.defectComment = newVal.defectComment ?? annotation.defectComment
    })
  }

  function addNewAnnotation(newAnnotation: ImageAnnotationType) {
    annotationsData.value = [...annotationsData.value, newAnnotation]
  }

  function commitUpdatedAnnotations(updatedAnnotations: Array<ImageAnnotationType>) {
    // Note: We still map here because we are essentially synchronizing state with the server/tool
    // and structural changes might occur.
    annotationsData.value = annotationsData.value
      .filter((existingAnnotation) => existingAnnotation.pendingDeletion !== true)
      .map((existingAnnotation) => {
        const matchingUpdatedAnnotation = updatedAnnotations.find(
          (_annotation) => existingAnnotation.annotationUuid === _annotation.annotationUuid
        )
        const returnVal =
          matchingUpdatedAnnotation !== undefined
            ? { ...existingAnnotation, ...matchingUpdatedAnnotation, isHovered: existingAnnotation.isHovered }
            : existingAnnotation

        return returnVal
      })

    //Keep staged annotations in sync with any changes made to annotation bounds
    //as bounds updates are not tied to the staging logic.
    stagedAnnotations.value = stagedAnnotations.value.map((annotation) => {
      const matchingUpdatedAnnotation = updatedAnnotations.find(
        (_annotation) => _annotation.annotationUuid === annotation.annotationUuid
      )
      if (matchingUpdatedAnnotation) {
        return {
          ...annotation,
          ...matchingUpdatedAnnotation,
        }
      } else return annotation
    })
  }

  function markAnnotationForDeletion(annotationUuid: string) {
    const annotation = annotationsData.value.find((a) => a.annotationUuid === annotationUuid)
    if (annotation) {
      annotation.pendingDeletion = true
    }
  }

  watch(
    () => stagedAnnotations.value,
    () => {
      if (
        (stagedAnnotations.value.length === 1 || isAllStagedAnnotationInSameGroup.value === true) &&
        (keyboardStore.isCtrlPressed === false || measurementStore.currentMeasurement !== undefined)
      ) {
        interfaceStore.isDefectDetailsVisible = true
      } else if (stagedAnnotations.value.length === 0) {
        interfaceStore.isDefectDetailsVisible = false
      }
    }
  )

  watch(
    () => markerStore.selectedMarkers,
    (newSelectedMarkers) => {
      // Create a map for the next staged state
      const nextStagedMap = new Map<string, ImageAnnotationType>()

      newSelectedMarkers.forEach((marker) => {
        const id = marker.state.meta?.id
        // Skip invalid IDs or measurement markers (if they shouldn't be staged as annotations)
        if (!id || marker.state.meta?.isMeasurement === true) return

        // If it was already staged, keep that instance (preserves unsaved edits in UI)
        const existingStaged = stagedAnnotations.value.find((a) => a.annotationUuid === id)

        if (existingStaged) {
          nextStagedMap.set(id, existingStaged)
        } else {
          // Otherwise fetch original and DEEP CLONE to protect source data
          const original = getAnnotationByUuid(id)
          if (original) {
            nextStagedMap.set(id, cloneDeep(original))
          }
        }
      })

      // 2. Preserve Temp annotations that are currently staged but might not have been picked up
      // by the selection logic if the marker interaction is slightly out of sync
      stagedAnnotations.value.forEach((annotation) => {
        if (annotation.isTemp && !nextStagedMap.has(annotation.annotationUuid)) {
          nextStagedMap.set(annotation.annotationUuid, annotation)
        }
      })

      // 3. Update state
      stagedAnnotations.value = Array.from(nextStagedMap.values())
    }
  )

  watch(
    () => markerStore.boundUpdatedMarkers,
    () => {
      if (!markerStore.boundUpdatedMarkers?.length) return

      const boundUpdatedAnnotationsMap = new Map()

      markerStore.boundUpdatedMarkers.forEach((marker) => {
        const markerKey = markerStore.getMarkerKey(marker)
        const correspondingAnnotation = getAnnotationByUuid(markerKey)
        if (!correspondingAnnotation) return

        let bounds
        if (marker.typeName === MarkerTypes.Rectangle) {
          bounds = getRectangleVertices(
            marker.state.position.x,
            marker.state.position.y,
            marker.state.dimensions.width,
            marker.state.dimensions.height
          )
        } else if (marker.typeName === MarkerTypes.Polygon) {
          bounds = getPointsVertices(marker.state.points)
        }

        const boundUpdatedAnnotation = {
          ...correspondingAnnotation,
          surfaceAreaStatus: MeasurementStatus.Pending,
          defectBounds: bounds,
        }

        boundUpdatedAnnotationsMap.set(boundUpdatedAnnotation.annotationUuid, boundUpdatedAnnotation)
        debounceManager.onMarkerInteraction(boundUpdatedAnnotation)
      })

      if (boundUpdatedAnnotationsMap.size > 0) {
        stagedAnnotations.value = stagedAnnotations.value.map(
          (annotation) => boundUpdatedAnnotationsMap.get(annotation.annotationUuid) || annotation
        )
      }
    }
  )

  watch(
    () => markerStore.hoveredMarkers,
    () => {
      const hoveredIds = new Set(markerStore.hoveredMarkers.map((m) => markerStore.getMarkerKey(m)))

      annotationsData.value.forEach((annotation) => {
        const shouldBeHovered = hoveredIds.has(annotation.annotationUuid)

        if (annotation.isHovered !== shouldBeHovered) {
          annotation.isHovered = shouldBeHovered
        }
      })
    }
  )

  return {
    annotations,
    confirmedAnnotations,
    suggestionsAnnotations,
    groupedAnnotationsMap,
    groupedAnnotationsArray,
    groupedSuggestionsMap,
    groupedSuggestionsArray,
    isAllStagedAnnotationInSameGroup,
    isStagedAnnotationsInDifferentGroups,
    isStagedAnnotationsConsistent,
    annotationsData,
    currentAnnotation,
    stagedAnnotations,
    groupAnnotations,
    createTempAnnotation,
    addNewAnnotation,
    removeAnnotations,
    removeAnnotationByUuid,
    removeTempAnnotation,
    setAnnotations,
    getAnnotationByUuid,
    getStagedAnnotationByUuid,
    clearHoveredAnnotations,
    clearStagedAnnotations,
    unstageAnnotationByUuid,
    stageAnnotationByUuid,
    unhoverAnnotationByUuid,
    hoverAnnotationByUuid,
    updateStagedAnnotationValues,
    commitUpdatedAnnotations,
    markAnnotationForDeletion,
  }
})
